import posthog from 'posthog-js';
import type { PostHogConfig } from 'posthog-js';
import { Capacitor } from '@capacitor/core';
import { ENV_CONFIG } from '../config/environment';
import type { User } from '../context/AuthContext';
import {
  BackofficeAnalyticsEvents,
  ProductAnalyticsEvents,
  ProductAnalyticsFunnels,
  ProductAnalyticsModules,
  type ProductAnalyticsFunnel,
} from './events';
import { resolvePostHogApiHost, resolvePostHogUiHost } from './posthogHosts';
import { buildLocaleMarketingProperties } from './localeMarketingAnalytics';

export const POSTHOG_PRO_GROUP_TYPE = 'pro';
export const POSTHOG_APP_SURFACE = 'backoffice';
export const POSTHOG_EMISSION_SOURCE_CLIENT = 'client';

let postHogReady = false;

export function isPostHogSupportedPlatform(): boolean {
  if (typeof window === 'undefined') return false;
  const isNativeIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  return !isNativeIOS;
}

export function isPostHogReady(): boolean {
  if (!postHogReady || !isPostHogSupportedPlatform()) return false;
  try {
    return !posthog.has_opted_out_capturing();
  } catch {
    return false;
  }
}

export function deriveActivationStage(user: User): string {
  if (user.is_premium) return 'premium';
  if (user.stripe_account_id?.trim()) return 'stripe_ready';
  if (user.onboarding_completed_at) return 'onboarded';
  if (user.pro_id) return 'profile_created';
  return 'signed_up';
}

export function getPostHogInitOptions(): Partial<PostHogConfig> {
  return {
    api_host: resolvePostHogApiHost(),
    ui_host: resolvePostHogUiHost(),
    person_profiles: 'identified_only',
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    enable_recording_console_log: false,
    persistence: 'localStorage+cookie',
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-ph-no-capture]',
    },
    loaded: () => {
      postHogReady = true;
      registerBackofficeSuperProperties();
    },
    debug: process.env.NODE_ENV === 'development' && process.env.REACT_APP_POSTHOG_DEBUG === 'true',
  };
}

export function initBackofficePostHog(apiKey: string): void {
  if (!apiKey || !isPostHogSupportedPlatform()) return;
  posthog.init(apiKey, getPostHogInitOptions());
}

export function optInBackofficePostHog(): void {
  if (!isPostHogSupportedPlatform()) return;
  try {
    posthog.opt_in_capturing();
    postHogReady = true;
    registerBackofficeSuperProperties();
  } catch {
    /* ignore */
  }
}

export function registerBackofficeSuperProperties(): void {
  if (!isPostHogReady()) return;

  try {
    posthog.register({
      app_surface: POSTHOG_APP_SURFACE,
      emission_source: POSTHOG_EMISSION_SOURCE_CLIENT,
      app_name: ENV_CONFIG.APP.NAME,
      app_version: ENV_CONFIG.APP.VERSION,
      environment: ENV_CONFIG.APP.ENVIRONMENT,
      platform: Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web',
    });
  } catch {
    /* Analytics must never break the app. */
  }
}

export function identifyBackofficeUser(user: User | null | undefined): void {
  if (!user?.user_id || !isPostHogReady()) return;

  try {
    const hasStripeAccount = Boolean(user.stripe_account_id?.trim());
    const onboardingCompleted = Boolean(user.onboarding_completed_at);
    const activationStage = deriveActivationStage(user);

    posthog.identify(`user_${user.user_id}`, {
      user_id: user.user_id,
      pro_id: user.pro_id ?? null,
      email: user.email,
      name: user.name,
      is_premium: !!user.is_premium,
      is_beta_tester: !!user.is_beta_tester,
      profile_complete: !!user.profile_complete,
      has_stripe_account: hasStripeAccount,
      onboarding_completed: onboardingCompleted,
      has_subscription_history: !!user.has_subscription_history,
      association_id: user.association_id ?? null,
      is_boarding_enabled: !!user.is_boarding_enabled,
      activation_stage: activationStage,
    });

    posthog.setPersonProperties({
      acquisition_channel: posthog.get_property('$virt_initial_channel_type'),
      initial_utm_source: posthog.get_property('$initial_utm_source'),
      initial_utm_campaign: posthog.get_property('$initial_utm_campaign'),
      initial_referring_domain: posthog.get_property('$initial_referring_domain'),
    });

    if (user.pro_id) {
      posthog.group(POSTHOG_PRO_GROUP_TYPE, String(user.pro_id), {
        pro_id: user.pro_id,
        has_stripe: hasStripeAccount,
        is_premium: !!user.is_premium,
        onboarding_completed: onboardingCompleted,
      });
    }

    posthog.register({
      is_premium: !!user.is_premium,
      pro_id: user.pro_id ?? null,
      user_id: user.user_id,
    });
  } catch {
    /* Analytics must never break auth flows. */
  }
}

export function captureBackofficeEvent(
  event: string,
  properties?: Record<string, unknown>
): void {
  if (!isPostHogReady()) return;

  try {
    posthog.capture(event, {
      app_surface: POSTHOG_APP_SURFACE,
      emission_source: POSTHOG_EMISSION_SOURCE_CLIENT,
      ...properties,
    });
  } catch {
    /* ignore */
  }
}

export function captureFunnelStep(
  event: string,
  funnel: ProductAnalyticsFunnel,
  step: string,
  properties?: Record<string, unknown>
): void {
  captureBackofficeEvent(event, {
    funnel,
    step,
    ...properties,
  });

  if (!isPostHogReady()) return;
  try {
    posthog.setPersonProperties({
      last_funnel_step: step,
      last_funnel: funnel,
    });
  } catch {
    /* ignore */
  }
}

/** Évite les doubles envois (re-render React, refresh page, etc.). */
export function captureFunnelStepOnce(
  dedupeKey: string,
  event: string,
  funnel: ProductAnalyticsFunnel,
  step: string,
  properties?: Record<string, unknown>
): boolean {
  if (typeof window !== 'undefined') {
    try {
      const storageKey = `ph_once:${dedupeKey}`;
      if (window.sessionStorage.getItem(storageKey) === '1') {
        return false;
      }
      window.sessionStorage.setItem(storageKey, '1');
    } catch {
      /* sessionStorage indisponible, on capture quand même */
    }
  }
  captureFunnelStep(event, funnel, step, properties);
  return true;
}

export function trackSubscriptionUpgradeClick(
  source: string,
  properties?: Record<string, unknown>
): void {
  captureFunnelStep(
    ProductAnalyticsEvents.SUBSCRIPTION_UPGRADE_CLICKED,
    ProductAnalyticsFunnels.MONETIZATION,
    'upgrade_clicked',
    {
      module: ProductAnalyticsModules.BILLING,
      source,
      ...properties,
    }
  );
}

export type PlanningViewPreference = 'modern' | 'classic' | 'capacity';

function setPlanningViewPersonProperty(view: PlanningViewPreference): void {
  if (!isPostHogReady()) return;
  try {
    posthog.setPersonProperties({ planning_view_preference: view });
  } catch {
    /* ignore */
  }
}

/** Vue planning active à l'ouverture de la page (ou après bascule). */
export function trackPlanningViewSeen(view: PlanningViewPreference): void {
  captureBackofficeEvent(ProductAnalyticsEvents.PLANNING_VIEW_SEEN, {
    module: ProductAnalyticsModules.PLANNING,
    planning_view: view,
  });
  setPlanningViewPersonProperty(view);
}

/** Création d'un planning récurrent (semaines types). */
export function trackAvailabilityScheduleCreated(props: {
  cycle_length: number;
  horizon_weeks: number;
  service_id: number;
}): void {
  captureBackofficeEvent(ProductAnalyticsEvents.AVAILABILITY_SCHEDULE_CREATED, {
    module: ProductAnalyticsModules.PLANNING,
    ...props,
  });
}

/** Export CSV aide registre E/S (Livre de Police boarding). */
export function trackPoliceRegisterExported(props?: {
  has_date_range?: boolean;
}): void {
  captureBackofficeEvent(ProductAnalyticsEvents.POLICE_REGISTER_EXPORTED, {
    module: ProductAnalyticsModules.ANIMALS,
    has_date_range: Boolean(props?.has_date_range),
  });
}

/** Bascule manuelle entre vues planning (classic / modern / capacity). */
export function trackPlanningViewToggled(
  toView: PlanningViewPreference,
  fromView?: PlanningViewPreference
): void {
  const resolvedFrom: PlanningViewPreference =
    fromView ?? (toView === 'modern' ? 'classic' : 'modern');
  captureBackofficeEvent(ProductAnalyticsEvents.PLANNING_VIEW_TOGGLED, {
    module: ProductAnalyticsModules.PLANNING,
    planning_view: toView,
    from_view: resolvedFrom,
    to_view: toView,
  });
  setPlanningViewPersonProperty(toView);
}

export type BlogArticleSharePlatform = 'twitter' | 'linkedin' | 'facebook' | 'copy' | 'native';
export type BlogArticleCtaType = 'signup' | 'booking' | 'pricing' | 'other';
export type BlogArticleScrollDepth = 25 | 50 | 75 | 100;

type BlogArticleBaseProps = {
  slug: string;
  categorySlug?: string | null;
  locale?: string | null;
};

/** Vue dédiée d'un article blog (site pro). */
export function trackBlogArticleView(props: BlogArticleBaseProps): void {
  captureBackofficeEvent(ProductAnalyticsEvents.BLOG_ARTICLE_VIEW, {
    slug: props.slug,
    site: 'pro',
    category_slug: props.categorySlug ?? null,
    locale: props.locale ?? null,
  });
}

/** Seuil de scroll atteint sur un article blog (site pro). */
export function trackBlogArticleScroll(
  props: BlogArticleBaseProps & { depth: BlogArticleScrollDepth }
): void {
  captureBackofficeEvent(ProductAnalyticsEvents.BLOG_ARTICLE_SCROLL, {
    slug: props.slug,
    site: 'pro',
    depth: props.depth,
    category_slug: props.categorySlug ?? null,
    locale: props.locale ?? null,
  });
}

/** Clic CTA depuis un article blog (site pro). */
export function trackBlogArticleCtaClicked(
  props: BlogArticleBaseProps & {
    ctaType: BlogArticleCtaType;
    destination?: string | null;
  }
): void {
  captureBackofficeEvent(ProductAnalyticsEvents.BLOG_ARTICLE_CTA_CLICKED, {
    slug: props.slug,
    site: 'pro',
    cta_type: props.ctaType,
    category_slug: props.categorySlug ?? null,
    destination: props.destination ?? null,
    locale: props.locale ?? null,
  });
}

/** Clic article lié depuis une page article (site pro). */
export function trackBlogArticleRelatedClicked(
  props: BlogArticleBaseProps & { targetSlug: string }
): void {
  captureBackofficeEvent(ProductAnalyticsEvents.BLOG_ARTICLE_RELATED_CLICKED, {
    slug: props.slug,
    site: 'pro',
    target_slug: props.targetSlug,
    category_slug: props.categorySlug ?? null,
    locale: props.locale ?? null,
  });
}

/** Partage d'un article blog (site pro). */
export function trackBlogArticleShare(props: {
  platform: BlogArticleSharePlatform;
  slug: string;
  locale?: string | null;
}): void {
  captureBackofficeEvent(ProductAnalyticsEvents.BLOG_ARTICLE_SHARE, {
    platform: props.platform,
    slug: props.slug,
    site: 'pro',
    locale: props.locale ?? null,
  });
}

/** Ouverture du chat Laika (respecte opt-out via isPostHogReady). */
export function trackLaikaChatOpened(source: string = 'unknown'): void {
  captureBackofficeEvent(ProductAnalyticsEvents.LAIKA_CHAT_OPENED, {
    module: ProductAnalyticsModules.LAIKA,
    source,
  });
}

/** Message utilisateur envoyé dans Laika. */
export function trackLaikaMessageSent(): void {
  captureBackofficeEvent(ProductAnalyticsEvents.LAIKA_MESSAGE_SENT, {
    module: ProductAnalyticsModules.LAIKA,
  });
}

/** Onglet hub Marketing affiché. */
export function trackMarketingHubTabViewed(tab: string): void {
  captureBackofficeEvent(ProductAnalyticsEvents.MARKETING_HUB_TAB_VIEWED, {
    module: ProductAnalyticsModules.MARKETING,
    tab,
  });
}

/** Section identité (Profil) visible. */
export function trackIdentitySectionViewed(): void {
  captureBackofficeEvent(ProductAnalyticsEvents.IDENTITY_SECTION_VIEWED, {
    module: ProductAnalyticsModules.MARKETING,
  });
}

/** Identité emails enregistrée. */
export function trackIdentitySaved(props: {
  header_mode: string;
  logo_source: string;
  accent_color: string;
  seed?: string | null;
}): void {
  captureBackofficeEvent(ProductAnalyticsEvents.IDENTITY_SAVED, {
    module: ProductAnalyticsModules.MARKETING,
    header_mode: props.header_mode,
    logo_source: props.logo_source,
    accent_color: props.accent_color,
    seed: props.seed ?? null,
  });
}

/** Seed 1-clic photo / logo facturation. */
export function trackIdentitySeedClicked(seed: 'profile' | 'billing'): void {
  captureBackofficeEvent(ProductAnalyticsEvents.IDENTITY_SEED_CLICKED, {
    module: ProductAnalyticsModules.MARKETING,
    seed,
  });
}

/** Clic CTA Configurer depuis une card Envois auto. */
export function trackOutboundConfigureClicked(props: {
  item_id: string;
  channel: string;
  configure_target: string | null;
  template_key?: string;
  automation_key?: string;
}): void {
  captureBackofficeEvent(ProductAnalyticsEvents.OUTBOUND_CONFIGURE_CLICKED, {
    module: ProductAnalyticsModules.MARKETING,
    item_id: props.item_id,
    channel: props.channel,
    configure_target: props.configure_target,
    template_key: props.template_key ?? null,
    automation_key: props.automation_key ?? null,
  });
}

/** Toggle enable/disable d’un auto-message. */
export function trackAutomationToggled(props: {
  trigger_key: string;
  enabled: boolean;
}): void {
  captureBackofficeEvent(ProductAnalyticsEvents.AUTOMATION_TOGGLED, {
    module: ProductAnalyticsModules.MARKETING,
    trigger_key: props.trigger_key,
    enabled: props.enabled,
  });
}

/** Légende (texte) copiée depuis Idées. */
export function trackMarketingIdeasCaptionCopied(captionId: string): void {
  captureBackofficeEvent(ProductAnalyticsEvents.MARKETING_IDEAS_CAPTION_COPIED, {
    module: ProductAnalyticsModules.MARKETING,
    caption_id: captionId,
  });
}

/** Texte places libres copié depuis Idées. */
export function trackMarketingIdeasSlotCopied(slotId?: string): void {
  captureBackofficeEvent(ProductAnalyticsEvents.MARKETING_IDEAS_SLOT_COPIED, {
    module: ProductAnalyticsModules.MARKETING,
    slot_id: slotId ?? null,
  });
}

/** Brouillon avis copié depuis Idées. */
export function trackMarketingIdeasReviewCopied(): void {
  captureBackofficeEvent(ProductAnalyticsEvents.MARKETING_IDEAS_REVIEW_COPIED, {
    module: ProductAnalyticsModules.MARKETING,
  });
}

/** Modal story image ouverte depuis Réseaux sociaux. */
export function trackMarketingIdeasStoryOpened(props: {
  source: 'hero' | 'phone' | 'section' | 'slot';
  month: string;
}): void {
  captureBackofficeEvent(ProductAnalyticsEvents.MARKETING_IDEAS_STORY_OPENED, {
    module: ProductAnalyticsModules.MARKETING,
    source: props.source,
    month: props.month,
  });
}

/** PNG story/feed téléchargé ou partagé. */
export function trackMarketingIdeasStoryDownloaded(props: {
  format: 'story' | 'feed';
  month: string;
  shared?: boolean;
}): void {
  captureBackofficeEvent(ProductAnalyticsEvents.MARKETING_IDEAS_STORY_DOWNLOADED, {
    module: ProductAnalyticsModules.MARKETING,
    format: props.format,
    month: props.month,
    shared: props.shared ?? false,
  });
}

/** PNG data (créneau ou chiens du mois) partagé ou téléchargé. */
export function trackDataPosterShared(props: {
  kind: 'slot' | 'month';
  shared: boolean;
}): void {
  captureBackofficeEvent(ProductAnalyticsEvents.DATA_POSTER_SHARED, {
    module: ProductAnalyticsModules.MARKETING,
    kind: props.kind,
    shared: props.shared,
  });
}

export function trackPartnerPageViewed(props: {
  partnerId: string;
  surface: 'public' | 'app';
  isPremium: boolean;
}): void {
  captureFunnelStep(
    ProductAnalyticsEvents.PARTNER_PAGE_VIEWED,
    ProductAnalyticsFunnels.MONETIZATION,
    'partner_page_viewed',
    {
      module: ProductAnalyticsModules.PARTNERS,
      partner_id: props.partnerId,
      surface: props.surface,
      is_premium: props.isPremium,
    }
  );
}

export function trackPartnerCodeUnlockedViewed(partnerId: string): void {
  captureFunnelStep(
    ProductAnalyticsEvents.PARTNER_CODE_UNLOCKED_VIEWED,
    ProductAnalyticsFunnels.MONETIZATION,
    'partner_code_unlocked_viewed',
    {
      module: ProductAnalyticsModules.PARTNERS,
      partner_id: partnerId,
    }
  );
}

export function trackPartnerUpgradeCtaClicked(props: {
  partnerId: string;
  source: string;
}): void {
  captureFunnelStep(
    ProductAnalyticsEvents.PARTNER_UPGRADE_CTA_CLICKED,
    ProductAnalyticsFunnels.MONETIZATION,
    'partner_upgrade_cta_clicked',
    {
      module: ProductAnalyticsModules.PARTNERS,
      partner_id: props.partnerId,
      source: props.source,
    }
  );
}

export function captureBackofficePageView(pathname: string, search: string): void {
  if (!isPostHogReady() || typeof window === 'undefined') return;

  const path = `${pathname}${search || ''}`;
  const routeZone = pathname.startsWith('/app') ? 'app' : 'public';

  let localeProps: Record<string, unknown> = {};
  if (routeZone === 'public') {
    try {
      localeProps = buildLocaleMarketingProperties(pathname);
      posthog.register({
        locale: localeProps.locale,
        marketing_surface: localeProps.marketing_surface,
      });
    } catch {
      /* ignore */
    }
  }

  try {
    posthog.capture(BackofficeAnalyticsEvents.PAGE_VIEW, {
      app_surface: POSTHOG_APP_SURFACE,
      emission_source: POSTHOG_EMISSION_SOURCE_CLIENT,
      pathname,
      route_zone: routeZone,
      search: search || undefined,
      $current_url: `${window.location.origin}${path}`,
      ...localeProps,
    });
  } catch {
    /* ignore */
  }
}

export function trackBackofficeLogin(user: User): void {
  captureFunnelStep(BackofficeAnalyticsEvents.USER_LOGGED_IN, ProductAnalyticsFunnels.PRO_ACTIVATION, 'logged_in', {
    pro_id: user.pro_id ?? null,
    is_premium: !!user.is_premium,
    profile_complete: !!user.profile_complete,
    user_id: user.user_id,
  });
  identifyBackofficeUser(user);
}

export function trackBackofficeLogout(): void {
  captureBackofficeEvent(BackofficeAnalyticsEvents.USER_LOGGED_OUT);
  resetBackofficePostHog();
}

export function resetBackofficePostHog(): void {
  postHogReady = false;
  try {
    posthog.reset();
  } catch {
    /* ignore */
  }
}

export function optOutBackofficePostHog(): void {
  postHogReady = false;
  try {
    posthog.opt_out_capturing();
  } catch {
    /* ignore */
  }
}
